/* eslint-disable s4l/require-display-name */
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Button, Typography } from '@s4l/ds'
import { IconArrowDown } from '@s4l/icons/IconArrowDown'
import { IconArrowUp } from '@s4l/icons/IconArrowUp'
import { IconInfo } from '@s4l/icons/IconInfo'
import { Devices, useBrowser } from '@s4l/utils/hooks/useBrowser'
import styles from '../styles.module.css'
import TaxFeesDetails from '../TaxFeesDetails'
import { type RoomData, type TaxDetail } from '../types'
import { groupTaxDetails, getTotalTaxAmount, getAllTaxDetails } from '../utils'
import { usePriceDetailsHelpers } from '../hooks'

export interface RoomSectionProps {
  room: RoomData
  index: number
  nights: number
  isMultiRoom: boolean
  isNewExperimentVariant?: boolean
  experimentVariant?: number
}

// Shared component for tax button with info icon
interface TaxButtonProps {
  taxDetails: TaxDetail[]
  isNewExperimentVariant?: boolean
  label: string
}

function TaxButton({ taxDetails, isNewExperimentVariant, label }: Readonly<TaxButtonProps>) {
  return (
    <TaxFeesDetails taxDetails={taxDetails} isNewExperimentVariant={isNewExperimentVariant}>
      <Button variant="text-flat" size="extraSmall" trailingIcon={<IconInfo size={16} />}>
        <Typography variant="body" size="l" bold>
          {label}
        </Typography>
      </Button>
    </TaxFeesDetails>
  )
}
TaxButton.displayName = 'TaxButton'

export default function RoomSection({
  room,
  index,
  nights,
  isMultiRoom,
  isNewExperimentVariant,
  experimentVariant,
}: Readonly<RoomSectionProps>) {
  const { getTaxDisplayName, formatCurrency, formatMessage } = usePriceDetailsHelpers()
  const { device } = useBrowser()

  const isV3 = experimentVariant === 3
  const isMobile = device === Devices.Mobile
  const isDesktop = device === Devices.Desktop

  // V3 Desktop: Collapsible state - open by default on desktop
  const [isTaxesOpen, setIsTaxesOpen] = useState(isDesktop)

  // Sync collapsible state when device detection updates
  useEffect(() => {
    if (isV3) {
      setIsTaxesOpen(isDesktop)
    }
  }, [isDesktop, isV3])

  // Toggle taxes collapsible (desktop V3 only)
  const handleTaxToggle = useCallback(() => {
    setIsTaxesOpen(prev => !prev)
  }, [])

  const roomName = isMultiRoom
    ? `${formatMessage({ id: 'global.room' })} ${index + 1}:`
    : formatMessage({ id: 'global.your-room' })

  const pricePerNight = room.subtotal / nights
  const pricePerNightFormatted = formatCurrency(pricePerNight, room.subtotalCurrency)

  // For V2 with discount, calculate original price before discount
  const originalSubtotal = room.discountAmount ? room.subtotal + room.discountAmount : room.subtotal
  const originalPricePerNight = originalSubtotal / nights
  const originalPricePerNightFormatted = formatCurrency(
    originalPricePerNight,
    room.subtotalCurrency
  )

  const allTaxDetails = useMemo(
    () => getAllTaxDetails(room.taxDetails, room.additionalFees),
    [room.taxDetails, room.additionalFees]
  )
  const groupedTaxDetails = useMemo(() => groupTaxDetails(allTaxDetails), [allTaxDetails])

  // Only show tax section if we have taxes AND they have actual amounts > 0
  const hasTaxDetails =
    groupedTaxDetails.length > 0 &&
    groupedTaxDetails.some(tax => (tax.client?.amount ?? tax.amount) > 0)

  const taxLabel = formatMessage({ id: 'global.taxes-fees' })
  const totalTaxAmount = formatCurrency(getTotalTaxAmount(allTaxDetails), room.totalCurrency)

  const taxDetailsContent = groupedTaxDetails.map(
    tax =>
      (tax.client?.amount || tax.amount > 0) && (
        <div className={styles.detailsRow} key={tax.name}>
          <Typography variant="caption" size="l" color="var(--color-neutral-600)">
            {`${getTaxDisplayName(tax.name)} (${tax.included ? formatMessage({ id: 'tax-breakdown.pay-upon-booking' }) : formatMessage({ id: 'tax-breakdown.pay-at-hotel' })})`}
          </Typography>
          <Typography variant="caption" size="l" color="var(--color-neutral-600)">
            {formatCurrency(tax.client?.amount ?? tax.amount, tax.client?.currency ?? tax.currency)}
          </Typography>
        </div>
      )
  )

  // Render room header based on variant
  const renderRoomHeader = () => {
    if (isV3) {
      // V3 format: "X nights x $price" → "$total"
      return (
        <div className={styles.roomHeader}>
          <Typography variant="body" size="l">
            {`${formatMessage({ id: 'global.nights' }, { nights })} x ${pricePerNightFormatted}`}
          </Typography>
          <Typography variant="body" size="l">
            {formatCurrency(room.subtotal, room.subtotalCurrency)}
          </Typography>
        </div>
      )
    }

    // V1/V2 format: "Your room: $price x X nights" → "$total"
    // For V2 with discount, show original price in title
    const displayPricePerNight =
      isNewExperimentVariant && room.discountAmount
        ? originalPricePerNightFormatted
        : pricePerNightFormatted
    const displaySubtotal =
      isNewExperimentVariant && room.discountAmount ? originalSubtotal : room.subtotal

    return (
      <div className={styles.roomHeader}>
        <div className={styles.titleWithDetails}>
          <Typography variant="body" size="l" bold>
            {roomName}
          </Typography>
          <Typography variant="body" size="l">
            {`${displayPricePerNight} x ${formatMessage({ id: 'global.nights' }, { nights })}`}
          </Typography>
        </div>
        <Typography variant="body" size="l">
          {formatCurrency(displaySubtotal, room.subtotalCurrency)}
        </Typography>
      </div>
    )
  }
  renderRoomHeader.displayName = 'renderRoomHeader'

  // Render tax section based on variant and device
  const renderTaxSection = () => {
    if (!hasTaxDetails) {
      return (
        <div className={styles.detailsBlock}>
          <div className={styles.detailsRowTitle}>
            <Typography variant="body" size="l">
              {taxLabel}
            </Typography>
            <Typography variant="body" size="l">
              {formatCurrency(0, room.totalCurrency)}
            </Typography>
          </div>
        </div>
      )
    }

    // V3 Desktop: Use collapsible with smooth animation
    if (isV3 && isDesktop) {
      return (
        <div className={styles.v3TaxSection}>
          <div className={styles.detailsRowTitle}>
            <button
              type="button"
              className={styles.v3TaxButton}
              onClick={handleTaxToggle}
              aria-expanded={isTaxesOpen}
            >
              <Typography variant="body" size="l" bold>
                {taxLabel}
              </Typography>
              {isTaxesOpen ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
            </button>
            <Typography variant="body" size="l">
              {totalTaxAmount}
            </Typography>
          </div>
          <div className={`${styles.collapsibleWrapper} ${isTaxesOpen ? styles.open : ''}`}>
            <div className={styles.collapsibleContent}>{taxDetailsContent}</div>
          </div>
        </div>
      )
    }

    // V3 Mobile: Use TaxFeesDetails button+modal (no sub-taxes shown below)
    if (isV3 && isMobile) {
      return (
        <div className={styles.v3TaxSection}>
          <div className={styles.detailsRowTitle}>
            <TaxButton
              taxDetails={groupedTaxDetails}
              isNewExperimentVariant={isNewExperimentVariant}
              label={taxLabel}
            />
            <Typography variant="body" size="l">
              {totalTaxAmount}
            </Typography>
          </div>
        </div>
      )
    }

    // V1/V2: Use TaxFeesDetails modal with individual tax lines shown below
    return (
      <div className={styles.detailsBlock}>
        <div className={styles.detailsRowTitle}>
          <TaxButton
            taxDetails={groupedTaxDetails}
            isNewExperimentVariant={isNewExperimentVariant}
            label={taxLabel}
          />
          <Typography variant="body" size="l">
            {totalTaxAmount}
          </Typography>
        </div>
        {taxDetailsContent}
      </div>
    )
  }
  renderTaxSection.displayName = 'renderTaxSection'

  return (
    <div className={styles.room}>
      {renderRoomHeader()}
      {renderTaxSection()}

      {/* V2 shows the special extra discount row */}
      {isNewExperimentVariant && room.discount && room.discountAmount && (
        <div className={styles.insuranceRow}>
          <Typography variant="body" size="l">
            {formatMessage({ id: 'price.details.discount' }, { discount: `-${room.discount}%` })}
          </Typography>
          <Typography variant="body" size="l" as="p" color="var(--color-success-500)" bold>
            {`-${formatCurrency(room.discountAmount, room.subtotalCurrency)}`}
          </Typography>
        </div>
      )}
    </div>
  )
}

RoomSection.displayName = 'RoomSection'
